/**
 * Library — unified knowledge hub with folder-based navigation.
 *
 * Consolidates content that used to live scattered across the Configurator
 * SaaS page (Fontes Ativas, Ferramentas de Terceiros, Plataformas — Prestadores
 * de Serviços, Agências B2B) and the Google Maps playbook, plus new folders
 * for internal playbooks. Each folder opens into a panel on the right.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Folder, FolderOpen, ChevronRight, MapPin, Radio, Wrench, Building2, Users,
  Loader2, ArrowRight, BookOpen, CheckCircle2, XCircle, Sparkles, Workflow, Radar,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useLang } from "../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Folder structure — each folder has an icon, a label, and a renderer.
const FOLDERS = [
  { id: "playbook-maps",  icon: MapPin,     color: "#3B82F6", kind: "playbook",    labelPt: "Playbook · Google Maps",         labelEn: "Playbook · Google Maps" },
  { id: "sources",        icon: Radio,      color: "#10B981", kind: "config",      field: "sources",   labelPt: "Fontes Ativas",                  labelEn: "Active Sources" },
  { id: "tools",          icon: Wrench,     color: "#F59E0B", kind: "config",      field: "tools",     labelPt: "Ferramentas de Terceiros",       labelEn: "Third-party Tools" },
  { id: "platforms",      icon: Building2,  color: "#A855F7", kind: "config",      field: "platforms", labelPt: "Plataformas · Prestadores",      labelEn: "Platforms · Service Providers" },
  { id: "agencies",       icon: Users,      color: "#EC4899", kind: "config",      field: "agencies",  labelPt: "Agências B2B",                   labelEn: "B2B Agencies" },
  { id: "playbook-kanban",icon: Workflow,   color: "#0EA5E9", kind: "howto",       labelPt: "How to · CRM Kanban",            labelEn: "How to · Kanban CRM" },
  { id: "playbook-b2c",   icon: Radar,      color: "#8B5CF6", kind: "howto",       labelPt: "How to · B2C Intent",            labelEn: "How to · B2C Intent" },
  { id: "playbook-ai",    icon: Sparkles,   color: "#C9A24E", kind: "howto",       labelPt: "How to · Auditoria GPT",      labelEn: "How to · GPT Audit" },
];

const HOWTO_CONTENT = {
  "playbook-kanban": {
    pt: { body: "Arraste leads entre as colunas Novo → Contactado → Qualificado → Proposta → Fechado. Cada card tem score, drawer com histórico, lookalike button e sequence modal.", steps: ["Selecione leads na Prospecção e clique 'Salvar'", "Arraste pelo Kanban conforme o lead avança", "Use o drawer para audits, e-mails e enriquecimento", "Crie sequências de cadence via Sequence Library"] },
    en: { body: "Drag leads between New → Contacted → Qualified → Proposal → Won columns. Each card has score, drawer with history, lookalike button and sequence modal.", steps: ["Pick leads from Prospecting and click 'Save'", "Drag across the Kanban as the lead advances", "Use the drawer for audits, emails and enrichment", "Build cadences via Sequence Library"] },
  },
  "playbook-b2c": {
    pt: { body: "Configure projetos monitorando Reddit + RSS + Web Alerts. O Intent Score classifica cada menção em [HOT ≥70], [WARM ≥40], [MONITOR ≥1].", steps: ["Crie um projeto em B2C Intent → Projects", "Adicione keywords por categoria (biblioteca alfabética)", "Inicie monitoramento — feed atualiza em minutos", "Top Intent Now aparece no Dashboard em tempo real"] },
    en: { body: "Set up projects monitoring Reddit + RSS + Web Alerts. Intent Score classifies each mention as [HOT ≥70], [WARM ≥40], [MONITOR ≥1].", steps: ["Create a project in B2C Intent → Projects", "Add keywords by category (alphabetical library)", "Start monitoring — feed updates in minutes", "Top Intent Now appears on Dashboard in real time"] },
  },
  "playbook-ai": {
    pt: { body: "Para cada lead salvo, clique em 'Run Audit' no Lead Drawer. GPT analisa site, SEO local, social, reviews e sugere um pitch em <90s.", steps: ["Abra o lead no Kanban CRM", "Clique Run Audit — aguarde ~60s", "Use o relatório como script de abertura", "Clique Send Email para disparo imediato"] },
    en: { body: "For each saved lead, click 'Run Audit' in the Lead Drawer. GPT analyzes site, local SEO, social, reviews and suggests a pitch in <90s.", steps: ["Open the lead in Kanban CRM", "Click Run Audit — wait ~60s", "Use the report as opening script", "Click Send Email to fire immediately"] },
  },
};

export default function Library() {
  const { lang } = useLang();
  const isPt = lang === "pt";
  const [active, setActive] = useState("playbook-maps");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => { setConfig(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const activeFolder = useMemo(() => FOLDERS.find((f) => f.id === active), [active]);

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="library-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)]">
        <header className="sticky top-0 z-30 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Logo className="w-9 h-9 object-contain" />
            <div className="min-w-0">
              <h1 className="text-[16px] font-semibold flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#3B82F6]" /> {isPt ? "Biblioteca" : "Library"}</h1>
              <p className="text-[12.5px] text-[var(--al-text-muted)] mt-0.5">{isPt ? "Playbooks, fontes, ferramentas e configurações organizadas em pastas." : "Playbooks, sources, tools and configuration organized in folders."}</p>
            </div>
          </div>
          <div className="flex items-center gap-2"><ThemeToggle /><LangToggle /></div>
        </header>

        <main className="px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-5 max-w-[1400px] mx-auto">
          {/* Folder tree */}
          <aside className="lg:col-span-3" data-testid="library-folders">
            <div className="al-card p-3 space-y-0.5 sticky top-[84px]">
              {FOLDERS.map((f) => {
                const Icon = active === f.id ? FolderOpen : Folder;
                const FIcon = f.icon;
                const isActive = active === f.id;
                return (
                  <button
                    type="button"
                    key={f.id}
                    data-testid={`library-folder-${f.id}`}
                    onClick={() => setActive(f.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-[12.5px] transition-all ${isActive ? "bg-[var(--al-bg)] font-bold" : "hover:bg-[var(--al-bg)]"}`}
                    style={isActive ? { borderLeft: `3px solid ${f.color}`, paddingLeft: "9px" } : undefined}>
                    <Icon className="w-4 h-4 shrink-0" style={{ color: isActive ? f.color : "#94A3B8" }} />
                    <FIcon className="w-3.5 h-3.5 shrink-0" style={{ color: f.color }} />
                    <span className="flex-1 truncate">{isPt ? f.labelPt : f.labelEn}</span>
                    {isActive && <ChevronRight className="w-3 h-3 shrink-0" style={{ color: f.color }} />}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Active folder panel */}
          <section className="lg:col-span-9" data-testid={`library-panel-${active}`}>
            {!activeFolder ? null : activeFolder.kind === "playbook" ? (
              <PlaybookGoogleMaps isPt={isPt} />
            ) : activeFolder.kind === "config" ? (
              loading ? (
                <div className="al-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-[#3B82F6]" /></div>
              ) : (
                <ConfigFolder isPt={isPt} folder={activeFolder} items={config?.[activeFolder.field] || []} />
              )
            ) : (
              <HowtoFolder isPt={isPt} folder={activeFolder} />
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

function ConfigFolder({ isPt, folder, items }) {
  const Icon = folder.icon;
  const on = items.filter((x) => x.active).length;
  return (
    <div className="al-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${folder.color}1A`, color: folder.color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-display text-[20px] font-bold tracking-tight">{isPt ? folder.labelPt : folder.labelEn}</h2>
          <p className="text-[12.5px] text-[var(--al-text-muted)]">{on} / {items.length} {isPt ? "ativas" : "active"}</p>
        </div>
      </div>
      <div className="divide-y divide-[var(--al-border)]">
        {items.map((it) => (
          <div key={it.name} className="flex items-start justify-between gap-4 py-3" data-testid={`library-item-${it.name}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13.5px]">{it.name}</div>
              {it.desc && <div className="text-[12px] text-[var(--al-text-muted)] mt-0.5 leading-snug">{it.desc}</div>}
            </div>
            {it.active ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700 shrink-0"><CheckCircle2 className="w-3 h-3" /> {isPt ? "SIM" : "ON"}</span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700 shrink-0"><XCircle className="w-3 h-3" /> {isPt ? "NÃO" : "OFF"}</span>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="py-10 text-center text-[12px] text-[var(--al-text-muted)]">{isPt ? "Nenhum item." : "No items."}</div>}
      </div>
    </div>
  );
}

function HowtoFolder({ isPt, folder }) {
  const Icon = folder.icon;
  const content = HOWTO_CONTENT[folder.id]?.[isPt ? "pt" : "en"];
  if (!content) return null;
  return (
    <div className="al-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${folder.color}1A`, color: folder.color }}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="font-display text-[20px] font-bold tracking-tight">{isPt ? folder.labelPt : folder.labelEn}</h2>
      </div>
      <p className="text-[14px] text-[var(--al-text)] leading-relaxed mb-4">{content.body}</p>
      <ol className="space-y-2.5 list-decimal ml-5">
        {content.steps.map((s) => <li key={s} className="text-[13px] leading-relaxed">{s}</li>)}
      </ol>
    </div>
  );
}

function PlaybookGoogleMaps({ isPt }) {
  const steps = isPt ? [
    { t: "Pesquisa direcionada por nicho", d: "Combine palavras-chave + cidade em Prospecção. Score Digital + badge QUENTE/MORNO/FRIO aplicados automaticamente." },
    { t: "Bulk Search em escala", d: "Passe uma lista de cidades e ative auto-save dos QUENTE. Sem CSV, sem extensão." },
    { t: "Enriquecimento automático", d: "Scraping em background detecta LinkedIn/Facebook/SSL/Blog. Recalcula score sem você fazer nada." },
    { t: "Abordagem personalizada com IA", d: "GPT gera auditoria em 6 seções + e-mail citando reviews reais. 2-3× mais respostas que cold mail genérico." },
  ] : [
    { t: "Niche-targeted search", d: "Combine keywords + city in Prospecting. Digital Score + HOT/WARM/COLD badge applied automatically." },
    { t: "Bulk Search at scale", d: "Paste a list of cities and enable auto-save for HOT leads. No CSV, no extension." },
    { t: "Automatic enrichment", d: "Background scraping detects LinkedIn/Facebook/SSL/Blog. Rescores without your input." },
    { t: "AI-personalized outreach", d: "GPT writes a 6-section audit + email citing real reviews. 2-3× more replies than generic cold mail." },
  ];
  return (
    <div className="space-y-4">
      <div className="al-card p-6" style={{ background: "linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-lg bg-[#3B82F6] text-white flex items-center justify-center">
            <MapPin className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-[22px] font-bold tracking-tight">{isPt ? "Playbook · Google Maps" : "Playbook · Google Maps"}</h2>
            <p className="text-[12.5px] text-[var(--al-text-muted)]">{isPt ? "Pesquisa · Automação · Enriquecimento · Abordagem" : "Search · Automation · Enrichment · Outreach"}</p>
          </div>
        </div>
        <p className="text-[13.5px] leading-relaxed">
          {isPt ? "Em vez de pular entre 5 ferramentas pagas, este playbook mostra como capturar, qualificar, enriquecer e abordar leads inteiramente dentro do AgenteLead Pro." : "Instead of juggling 5 paid tools, this playbook walks you through capturing, qualifying, enriching and outreaching leads entirely inside AgenteLead Pro."}
        </p>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[#3B82F6] text-white text-[12.5px] font-semibold hover:bg-[#2563EB]">{isPt ? "Começar a Prospectar" : "Start Prospecting"} <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {steps.map((s, i) => (
          <div key={s.t} className="al-card p-5">
            <div className="text-[44px] font-display font-black leading-none text-[#E5E2DA]">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="mt-2 font-display text-[16px] font-bold tracking-tight">{s.t}</h3>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      <div className="al-card p-5" data-testid="library-native-features">
        <h3 className="font-display text-[14px] font-bold mb-3">{isPt ? "Continue dentro do AgenteLead Pro" : "Continue inside AgenteLead Pro"}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {[
            { n: "CRM Kanban",     to: "/kanban" },
            { n: "Integrações",    to: "/integrations" },
            { n: "Webhooks",       to: "/webhooks" },
            { n: "Activity Log",   to: "/log" },
            { n: "Dashboard",      to: "/dashboard" },
            { n: "Sequence Lib.",  to: "/kanban" },
          ].map((f) => (
            <Link key={f.n} to={f.to} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[var(--al-border)] hover:border-[#3B82F6] hover:bg-[#EFF6FF]/30 text-[12.5px] font-semibold transition-colors">
              {f.n}<ArrowRight className="w-3 h-3 ml-auto text-[var(--al-text-muted)]" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
